import { Badge, Button, Form, Modal, Space, Switch, Table } from "antd";
import execute from "hooks/execute";
import { useEffect, useMemo, useState } from "react";
import { FaFilePen, FaPlus, FaTrash } from "react-icons/fa6";
import { DataEditor } from "./DataEditor";
import DeleteConfirmationModal from "../../../components/shared/DeleteConfirmationModal";
import { protectedResources } from "config/msalConfig";

const RoleValueTable = ({ customer }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState([]);

    const [editableDataItem, setEditableDataItem] = useState({});
    const [isDataEditorOpen, setIsDataEditorOpen] = useState(false);
    const [statusForm] = Form.useForm();

    const [currentRecord, setCurrentRecord] = useState(null);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isStatusEditorOpen, setStatusEditorOpen] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);

    const openEditorToAdd = () => {
        setEditableDataItem({ UserId: customer.UserID });
        setIsDataEditorOpen(true);
    };

    const handleDataEditFinished = () => {
        setIsDataEditorOpen(false);
        fetchData();
    };

    const handleDataEditCancelled = () => {
        setIsDataEditorOpen(false);
    };

    const handleDelete = (record) => {
        setCurrentRecord({ ...record });
        setDeleteModalOpen(true);
    };

    const handleEditStatus = (record) => {
        setCurrentRecord({ ...record });
        statusForm.setFieldsValue({
            Status: Number(record.AssignmentStatus) === 1
        });
        setStatusEditorOpen(true);
    };

    const roleFilterOptions = useMemo(
        () =>
            Array.from(new Set((data || []).map((item) => item.RoleName).filter(Boolean)))
                .sort((a, b) => a.localeCompare(b))
                .map((name) => ({ text: name, value: name })),
        [data]
    );

    const moduleFilterOptions = useMemo(() => {
        const moduleTokens = new Set();
        (data || []).forEach((item) => {
            (item.RoleModules || '')
                .split(',')
                .map((moduleName) => moduleName.trim())
                .filter(Boolean)
                .forEach((moduleName) => moduleTokens.add(moduleName));
        });

        return Array.from(moduleTokens)
            .sort((a, b) => a.localeCompare(b))
            .map((moduleName) => ({ text: moduleName, value: moduleName }));
    }, [data]);

    const columns = [
        {
            title: 'Role',
            dataIndex: 'RoleName',
            key: 'RoleName',
            sorter: (a, b) => (a.RoleName || '').localeCompare(b.RoleName || ''),
            filters: roleFilterOptions,
            onFilter: (value, record) => (record.RoleName || '') === value
        },
        {
            title: 'Modules',
            dataIndex: 'RoleModules',
            key: 'RoleModules',
            sorter: (a, b) => (a.RoleModules || '').localeCompare(b.RoleModules || ''),
            filters: moduleFilterOptions,
            onFilter: (value, record) => {
                const modules = (record.RoleModules || '')
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
                return modules.includes(value);
            }
        },
        {
            title: 'Status',
            dataIndex: 'AssignmentStatus',
            key: 'AssignmentStatus',
            filters: [
                { text: 'Active', value: 1 },
                { text: 'Inactive', value: 0 }
            ],
            onFilter: (value, record) => Number(record.AssignmentStatus) === Number(value),
            render: (_, record) =>
                Number(record.AssignmentStatus) === 1 ? (
                    <Badge status="success" text="Active" />
                ) : (
                    <Badge status="default" text="Inactive" />
                )
        },
        {
            title: (
                <Button
                    color="default"
                    variant="text"
                    onClick={openEditorToAdd}
                    icon={<FaPlus style={{ color: '#ffad1c' }} />}
                />
            ),
            key: 'actions',
            className: "text-end-imp",
            render: (_, record) => (
                <>
                    <Button
                        color="default"
                        variant="text"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditStatus(record);
                        }}
                        icon={<FaFilePen style={{ color: '#ffad1c' }} />}
                    />
                    <Button
                        color="default"
                        variant="text"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(record);
                        }}
                        icon={<FaTrash style={{ color: '#ffad1c' }} />}
                    />
                </>
            ),
        },
    ];

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await execute('GET', `${protectedResources.api.baseEndPoint}/customeUserRoles/${customer.UserID}/values`);
            if (response?.success) {
                setData(response.data || []);
                setError('');
            } else {
                setData([]);
                setError(response?.message || 'No role assignments found');
            }
        } catch (err) {
            setError('Failed to fetch data: ' + err.message);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (customer) {
            fetchData();
        }
    }, [customer]);

    const handleConfirmDelete = async () => {
        try {
            const response = await execute('DELETE', `${protectedResources.api.baseEndPoint}/customeUserRolesValues/${currentRecord.Id}`);
            if (response?.success) {
                await fetchData();
            } else {
                setError(response?.message || 'Failed to delete role assignment');
            }
        } catch (error) {
            setError('Failed to delete role assignment: ' + error.message);
        }
        setDeleteModalOpen(false);
    };

    const handleUpdateStatus = async (values) => {
        try {
            setSavingStatus(true);
            const response = await execute(
                'PUT',
                `${protectedResources.api.baseEndPoint}/customeUserRolesValues/${currentRecord.Id}/status`,
                { Status: values.Status ? 1 : 0 }
            );

            if (response?.success) {
                await fetchData();
                setStatusEditorOpen(false);
            } else {
                setError(response?.message || 'Failed to update role assignment status');
            }
        } catch (error) {
            setError('Failed to update role assignment status: ' + error.message);
        } finally {
            setSavingStatus(false);
        }
    };

    return (
        <>
            <Table
                rowKey={(record) => record.Id}
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={false}
            />
            {isDataEditorOpen ? (
                <DataEditor
                    customer={customer}
                    isOpen={isDataEditorOpen}
                    dataItem={editableDataItem}
                    onDataEditorFinished={handleDataEditFinished}
                    onDataEditorCancelled={handleDataEditCancelled}
                />
            ) : null}
            {isDeleteModalOpen ? (
                <DeleteConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    record={currentRecord}
                    onDeleteConfirmed={handleConfirmDelete}
                />
            ) : null}
            <Modal
                open={isStatusEditorOpen}
                title="Edit CustomerUser Role Assignment"
                onCancel={() => setStatusEditorOpen(false)}
                okText={<Space>Save</Space>}
                cancelText={<Space>Cancel</Space>}
                okButtonProps={{ loading: savingStatus, autoFocus: true, htmlType: 'submit' }}
                destroyOnClose
                modalRender={(dom) => (
                    <Form
                        form={statusForm}
                        layout="horizontal"
                        labelCol={{ span: 7 }}
                        wrapperCol={{ span: 17 }}
                        onFinish={handleUpdateStatus}
                    >
                        {dom}
                    </Form>
                )}
            >
                <Form.Item label="Role">
                    <span>{currentRecord?.RoleName || '-'}</span>
                </Form.Item>
                <Form.Item label="Modules">
                    <span>{currentRecord?.RoleModules || '-'}</span>
                </Form.Item>
                <Form.Item
                    name="Status"
                    label="Status"
                    valuePropName="checked"
                >
                    <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
            </Modal>
            {error ? <div style={{ color: '#d14343', marginTop: 8 }}>{error}</div> : null}
        </>
    );
};

export default RoleValueTable;
